import { queryOptions } from "@tanstack/react-query";
import { Schema } from "effect";
import { decodeUnknownOption } from "../../../packages/common/src/schema.ts";
import { OkLocationDirectory } from "../../../packages/common/src/schemas/locations.ts";
import { type StaffApi, ApiError } from "#api.ts";
import { runEffectQuery } from "#effect-query.ts";
import type { Order } from "#orders.ts";

/** Missing locations remain explicitly selectable rather than guessed from devices. */
export const unassignedLocation = "__unassigned";

/** Selections are ORed; no selection includes every location. */
export function matchesLocation(
  order: Order,
  selected: readonly string[],
): boolean {
  return (
    selected.length === 0 ||
    selected.includes(order.locationId ?? unassignedLocation)
  );
}

const directoryCacheSchema = Schema.Struct({
  updatedAt: Schema.Number.pipe(Schema.int(), Schema.greaterThan(0)),
  directory: OkLocationDirectory,
});

function readStored<A, I>(
  key: string,
  schema: Schema.Schema<A, I, never>,
): A | undefined {
  try {
    const value = localStorage.getItem(key);
    return value ? decodeUnknownOption(schema, JSON.parse(value)) : undefined;
  } catch {
    // Private browsing, full storage, and corrupt old caches must not block service.
    return undefined;
  }
}

function store(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Optional offline cache. */
  }
}

/** One directory request per stale session (12 hours); cached names render before background refresh. */
export const locationsQueryOptions = (
  api: StaffApi,
  userId: string | undefined,
) => {
  const key = `landpad.locations.v1:${api.config.apiBase}:${userId}`;
  const cached = readStored(key, directoryCacheSchema);
  return queryOptions<typeof OkLocationDirectory.Type, ApiError>({
    queryKey: ["location-directory", api.config.apiBase, userId],
    enabled: userId !== undefined,
    staleTime: 12 * 60 * 60_000,
    gcTime: 24 * 60 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    initialData: cached?.directory,
    initialDataUpdatedAt: cached
      ? Math.min(cached.updatedAt, Date.now())
      : undefined,
    queryFn: async ({ signal }) => {
      const directory = await runEffectQuery(
        api.requestEffect({
          path: "/merchants/locations",
          schema: OkLocationDirectory,
          signal,
        }),
        signal,
      );
      store(key, { updatedAt: Date.now(), directory });
      return directory;
    },
  });
};
